import {
	AsapMarker,
	OrderingTypes,
	PaymentTypeNames,
	PaymentTypePriorities,
	PaymentTypes,
} from 'new-constants/common.constants';
import {getCartCategories, getCartItems, getCartItemSum, getCartMainCategories} from 'helpers/order-line.helper.ts';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';
import isEqual from 'lodash/isEqual';
import {getSafeTranslation} from 'helpers/localization.helper.ts';
import {getCategoryFromMainCategories, getMenuEntry} from 'helpers/menu.helper';
import {CartItem, OrderLine} from './order-line.helper.types';
import {
	DiscountRequest,
	PaymentTypeId,
	PurchaseToken,
	ServerOrder, SubPaymentType, SubPaymentTypeId,
	TableNumber
} from './purchase.helper.types';
import {Menu} from './menu.helper.types';
import {PurchaseCacheEntry} from '../reducers/purchase.reducer.types';
import {DeliveryAddressState} from '../reducers/delivery-address.reducer.types';
import {OrderingModeState} from '../reducers/ordering-mode.reducer.types';
import {Restaurant, RestaurantId} from './restaurant.helper.types';
import {OrderTimeState} from '../reducers/order-time.reducer.types';
import {Translations} from './localization.helper.types';

export const getTipAmount = (cartTotal, tipPercent) => {
	return (cartTotal / 100) * tipPercent;
};

export const getCartTotal = (currentPurchase, currentServerValues, deliveryConditions, cartItems, tipAmount) => {
	const discountPrice =
		currentPurchase && typeof currentServerValues.discountPrice === 'number'
			? currentServerValues.discountPrice
			: getCartItemSum(cartItems) + (deliveryConditions?.price || 0);

	return (
		(cartItems.length > 0 ? discountPrice : 0) +
		(tipAmount || 0) -
		(currentPurchase && currentPurchase.useCashPoints && currentServerValues.cashpointsAmount > 0
			? currentServerValues.cashpointsAmount
			: 0)
	);
};

export const getCartDataForDiscount = (
	currentRestaurantId: RestaurantId,
	orderingMode: OrderingModeState,
	orderTime: OrderTimeState,
	currentOrderLines: Array<OrderLine>,
	currentMenu: Menu,
	currentPurchase: PurchaseCacheEntry,
	currentDeliveryAddress: DeliveryAddressState,
	currentTableNumber: TableNumber,
	tipAmount: number
): DiscountRequest => {
	if (currentOrderLines.length <= 0) {
		return null;
	}

	const isHomeDelivery = orderingMode.orderingType === OrderingTypes.delivery;
	const cartItems = getCartItems(currentOrderLines, currentMenu);

	//check discount
	//orderLines
	return {
		orderLines: currentOrderLines.map(orderLine => {
			return {
				menuEntryId: orderLine.menuEntryId,
				modifierSelections: orderLine.modifierSelections,
				modifiers: {//DEBUG Old prop for compatibility
					groups: Object.keys(orderLine.modifierSelections)
						.reduce((accum, modifierGroupId) => ({
							...accum,
							[modifierGroupId]: orderLine.modifierSelections[modifierGroupId]
								.map(modifierSelection => modifierSelection.modifierId)
						}), {}),
				},
				quantity: orderLine.quantity,
			};
		}),
		restaurantId: currentRestaurantId,
		isHomeDelivery,
		deliveryZip: currentDeliveryAddress?.zip || undefined,
		includeDeliveryPrice: isHomeDelivery,
		tip: tipAmount,
		pickupData: {
			isAsap: !!(orderTime.asap && orderTime.pickupTime && orderTime.pickupTime === AsapMarker),
			pickupTime: orderTime.pickupTime,
			pickupDate: orderTime.pickupDate,
		},
		tableNumber: currentTableNumber,
		currentTotal: getCartItemSum(cartItems)
	};
};

function getOverridenPaymentTypes(cartItems: Array<CartItem>, currentMenu: Menu): Array<PaymentTypeId> {

	const cartMainCategories = getCartMainCategories(cartItems, currentMenu);

	//@ts-ignore
	return uniq(
		flatten(
			cartMainCategories.map(mainCategory => mainCategory.paymentTypes)
		)
	)
		.filter(paymentType => !!paymentType);
}

function getSortedPaymentTypes(paymentTypes) {
	return paymentTypes.sort(
		(paymentType1, paymentType2) => PaymentTypePriorities[paymentType1] - PaymentTypePriorities[paymentType2]
	);
}

export const getAvailablePaymentTypes = (
	currentMenu,
	restaurantPaymentTypes,
	cartItems,
	discountPrice,
	tipAmount = 0,
	useCashPoints,
	cashPointsAmount,
	cartItemsNeedToBeRemoved
) => {
	if (cartItems.length <= 0) {
		return [];
	}

	if (typeof discountPrice !== 'number' && !cartItemsNeedToBeRemoved) {
		return [];
	}

	if (useCashPoints && cashPointsAmount >= discountPrice + tipAmount) {
		return [PaymentTypes.payWithCashPoints];
	}

	if (discountPrice + tipAmount === 0 && !cartItemsNeedToBeRemoved) {
		return [PaymentTypes.free];
	}

	let resultPaymentTypes = restaurantPaymentTypes || [];

	const overridenPaymentTypes = getOverridenPaymentTypes(cartItems, currentMenu);

	if (overridenPaymentTypes.length > 0) {
		resultPaymentTypes = overridenPaymentTypes;
	}

	return getSortedPaymentTypes([
		...resultPaymentTypes.filter(
			paymentType => paymentType !== PaymentTypes.payWithCashPoints && paymentType !== PaymentTypes.free
		),
	]);
};

export const getPaymentTypeName = (
	paymentTypeId: PaymentTypeId,
	subPaymentTypeName: string,
	cashpointsName: string,
	translations: Translations = {}
): string => {

	if (paymentTypeId === PaymentTypes.customPaymentType) {
		return subPaymentTypeName || '';
	}

	let paymentTypeNameKey = PaymentTypeNames.find(paymentTypeName => {
		return paymentTypeName.id === paymentTypeId;
	});

	if (paymentTypeNameKey) {
		return getSafeTranslation(paymentTypeNameKey.appResourceKey, translations).replace(
			'{cashpointsName}',
			cashpointsName
		);
	} else {
		return '';
	}
};

export const getCurrentPaymentType = (userSelectedPaymentType, availablePaymentTypes) => {
	return availablePaymentTypes.includes(userSelectedPaymentType)
		? userSelectedPaymentType
		: availablePaymentTypes.length > 0 && availablePaymentTypes[0];
};

export const getAssembledOrder = (
	currentOrderLines: Array<OrderLine>,
	currentPurchase: PurchaseCacheEntry,
	currentRestaurant: Restaurant,
	orderTime: OrderTimeState,
	orderingMode: OrderingModeState,
	currentPaymentType: PaymentTypeId,
	currentSubPaymentTypeId: SubPaymentTypeId,
	currentDeliveryAddress: DeliveryAddressState,
	currentMenu: Menu,
	currentTableNumber: TableNumber,
	purchaseToken: PurchaseToken,
	tipAmount: number
): ServerOrder => {
	if (!currentOrderLines || currentOrderLines.length < 1) {
		return null;
	}

	const isAsap = orderTime.asap && orderTime.pickupTime && orderTime.pickupTime === AsapMarker;

	return {
		orderLines: currentOrderLines.map(orderLine => ({
			menuEntryId: orderLine.menuEntryId,
			modifierSelections: orderLine.modifierSelections,
			modifiers: {//DEBUG Old prop for compatibility
				groups: Object.keys(orderLine.modifierSelections)
					.reduce((accum, modifierGroupId) => ({
						...accum,
						[modifierGroupId]: orderLine.modifierSelections[modifierGroupId]
							.map(modifierSelection => modifierSelection.modifierId)
					}), {}),
			},
			quantity: orderLine.quantity,
		})),
		restaurantId: currentRestaurant?.restaurantId,
		discountGroup: '', //TODO
		useCashPoints: currentPurchase?.useCashPoints || false,
		tip: tipAmount || 0,
		paymentType: currentPaymentType,
		subPaymentTypeId: currentSubPaymentTypeId,
		orderMessage: currentPurchase?.comment || '',
		token: purchaseToken,
		numberOfPeople: currentPurchase?.cutlery || 0,
		pickupData: {
			isAsap,
			pickupTime: isAsap ? null : orderTime.pickupTime,
			pickupDate: orderTime.pickupDate?.replace(/(\d{2})\.(\d{2})\.(\d{4})/, '$3.$2.$1'),
		},
		deliveryData: {
			number: currentTableNumber,
			address: currentDeliveryAddress?.address,
			isHomeDelivery: orderingMode.orderingType === OrderingTypes.delivery,
			instructions: '', //compatibility
			zip: currentDeliveryAddress?.zip,
			isEatIn: orderingMode.orderingType === OrderingTypes.dineIn,
		},
	};
};

function isMainCategoriesCompatible(mainCategory, cartMainCategory) {
	return cartMainCategory.vendorGroup === mainCategory.vendorGroup &&
		isEqual(cartMainCategory.paymentTypes, mainCategory.paymentTypes);
}

function isCategoriesCompatible(category, cartCategory) {
	return category.doNotSellWithOtherCategories || cartCategory.doNotSellWithOtherCategories
		? category.id === cartCategory.id
		: true;
}

export const isMenuEntryCompatibleWithCart = (entryId, cartItems, currentMenu) => {
	const menuEntry = getMenuEntry(currentMenu.menu.menu, entryId);

	const cartMainCategories = getCartMainCategories(cartItems, currentMenu);
	const entryMainCategory = currentMenu.menu.menu.find(mainCategory => mainCategory.id === menuEntry.mainCategoryId);

	if (
		!cartMainCategories.reduce(
			(accum, cartMainCategory) => accum && isMainCategoriesCompatible(entryMainCategory, cartMainCategory),
			true
		)
	) {
		return false;
	}

	const cartCategories = getCartCategories(cartItems, currentMenu);
	const entryCategory = getCategoryFromMainCategories(currentMenu.menu.menu, menuEntry.categoryId);

	if (
		!cartCategories.reduce((accum, cartCategory) => accum && isCategoriesCompatible(entryCategory, cartCategory), true)
	) {
		return false;
	}

	return true;
};

export const roundedUpTip = (total) => {
	if (total < 10) {
		return Math.ceil(total + 0.1 * total);
	} else if (total >= 10 && total < 200) {
		const totalPercent = total + 0.1 * total;
		const remainder = totalPercent % 5;
		return (5 - remainder + totalPercent);
	} else if (total >= 200 && total < 900) {
		const totalPercent = total + 0.1 * total;
		const remainder = totalPercent % 50;
		return (50 - remainder + totalPercent);
	} else if (total >= 900) {
		const totalPercent = total + 0.05 * total;
		const remainder = totalPercent % 100;
		return (100 - remainder + totalPercent);
	}

};
